/**
 * S5 — Entity (/agg/entity/group/:groupName), with the C1 Sharing drawer.
 *
 * Rebuilt whole-screen to the approved figure (Story #5648 build v2): one
 * entity's inventions as far as its owners have shared them with this
 * portfolio. Header with owner chips and the two actions, the next-step
 * banner, then two columns — Inventions, Entity map and Disclosures on the
 * left; What you can see, Yours — not part of Portfolio, and Shared with this
 * portfolio on the right.
 *
 * The page no longer draws its own breadcrumb row: it declares its place on
 * the spine and the shell renders the one crumb line.
 */
import { useMemo, useState } from 'react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Mail, Star } from 'lucide-react'
import { useAggregator } from '../../contexts/AggregatorContext'
import HelpPopover from '../../components/help/HelpPopover'
import { NextStepBanner, useDefaultLevels, useSetSpine } from '../../components/spine'
import {
  askEntityToShareMore,
  endSponsorship,
  getAggregatorDisclosures,
  getAggregatorEntities,
  getAggregatorPortfolioProjects,
  getPortfolioMap,
  getPortfolioOverlap,
  getProjects,
  listEntityIpArrangements,
  proposeIpArrangement,
  setExtendedVisibility,
} from '../../services/api'
import {
  buildEntityGroups, entityGroupPath, findEntityGroup, otherOwnerLabel,
} from '../../components/aggregator/entityGroups'
import EntityInventions from '../../components/aggregator/EntityInventions'
import EntityMap from '../../components/aggregator/EntityMap'
import EntityDisclosures from '../../components/aggregator/EntityDisclosures'
import WhatYouCanSee from '../../components/aggregator/WhatYouCanSee'
import SharedWithPortfolio from '../../components/aggregator/SharedWithPortfolio'
import SharingEditor from '../../components/aggregator/SharingEditor'
import YoursNotPartOfPortfolio from '../../components/aggregator/YoursNotPartOfPortfolio'
import EntityBadge from '../../components/aggregator/EntityBadge'
import { IpArrangementPill } from '../../components/sharing/IpArrangementSection'
import { LIVE_STATUSES, currentArrangement } from '../../components/sharing/ipModel'
import { buildDisclosureRows } from '../../components/aggregator/disclosureRows'
import { intakeFor, intakeForGroup, useIntakeTags } from '../../components/aggregator/intake'
import { useEntityGroupNames } from '../../components/aggregator/entityGroupNames'
import { byInvitationTitle, entitySharing } from '../../components/aggregator/sharing'
import type { SharingEntity } from '../../components/aggregator/sharing'
import type { AggregatorProject, DisclosureAgreement } from '../../types'
import type { IpArrangementHolderRow } from '../../types/ipArrangements'

/**
 * The one next step at Entity, from live state and in this order: an
 * unreviewed inbound invention first, then an owner who has not yet accepted
 * the invitation, then a consent this entity owes on a ladder. When none of
 * those is true there is genuinely nothing waiting and the banner says so.
 */
export type EntityNextStep =
  | { kind: 'triage'; project: AggregatorProject }
  | { kind: 'awaiting-acceptance'; ownerName: string }
  | { kind: 'consent'; counterparty: string }
  | { kind: 'none' }

export function rankEntityNextStep(
  projects: AggregatorProject[],
  members: SharingEntity[],
  consentOwedTo: string | null,
): EntityNextStep {
  const inbound = projects.find((p) => p.review_status === 'pending')
  if (inbound) return { kind: 'triage', project: inbound }
  const pending = members.find((m) => m.status === 'pending')
  if (pending) return { kind: 'awaiting-acceptance', ownerName: pending.entity_name }
  if (consentOwedTo) return { kind: 'consent', counterparty: consentOwedTo }
  return { kind: 'none' }
}

export default function EntityGroupView() {
  const { groupName = '' } = useParams<{ groupName: string }>()
  const label = useMemo(() => decodeURIComponent(groupName), [groupName])
  const [searchParams, setSearchParams] = useSearchParams()
  const { activeAggregatorId: aggregatorId, memberships } = useAggregator()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [askState, setAskState] = useState<'idle' | 'sent' | 'too-soon' | 'failed'>('idle')
  // The notches this viewer's ROLE gets — never a list this page decides.
  const levels = useDefaultLevels()

  const membership = memberships.find((m) => m.aggregator_id === aggregatorId)
  const portfolioName = membership?.aggregator_name ?? ''
  const canEndRelationship = membership?.role === 'billing_owner'

  const {
    data: portfolio,
    isLoading: portfolioLoading,
    isError: portfolioError,
  } = useQuery({
    queryKey: ['aggregator-portfolio', aggregatorId],
    queryFn: () => getAggregatorPortfolioProjects(aggregatorId),
    enabled: !!aggregatorId,
  })
  const {
    data: entitiesData,
    isLoading: entitiesLoading,
    isError: entitiesError,
  } = useQuery({
    queryKey: ['aggregator-entities', aggregatorId],
    queryFn: () => getAggregatorEntities(aggregatorId),
    enabled: !!aggregatorId,
  })
  const { data: disclosuresData } = useQuery({
    queryKey: ['aggregatorDisclosures', aggregatorId],
    queryFn: () => getAggregatorDisclosures(aggregatorId ?? ''),
    enabled: !!aggregatorId,
  })
  // The SAME layout the Portfolio Atlas draws — one query key, so the entity
  // map costs no extra fetch once the atlas has been opened, and can never
  // show a different projection from it.
  const { data: mapData } = useQuery({
    queryKey: ['portfolio-map', aggregatorId],
    queryFn: () => getPortfolioMap(aggregatorId),
    enabled: !!aggregatorId,
  })
  const { data: ownProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
    enabled: !!aggregatorId,
  })
  // The SAME overlap groups the Overlap view and the S6 rail card read, under
  // the SAME query key, so the amber ellipse on this map and the count on the
  // Portfolio screen can never disagree — and so opening one after the other
  // costs no second request.
  const { data: overlapData } = useQuery({
    queryKey: ['portfolio-overlap', aggregatorId],
    queryFn: () => getPortfolioOverlap(aggregatorId),
    enabled: !!aggregatorId,
  })

  const { intakeTags, setIntake } = useIntakeTags(aggregatorId)
  // Figure C1 draws Entity group as an editable field. It is the PORTFOLIO's
  // own label for the inventor, so it saves exactly the way the intake beside
  // it does — and it is applied inside buildEntityGroups, so S5 and S6 group
  // the same inventor the same way.
  const { groupNames, setGroupName } = useEntityGroupNames(aggregatorId)

  const entities = useMemo(() => (entitiesData?.entities ?? []) as SharingEntity[], [entitiesData])
  const projects = useMemo(() => portfolio?.projects ?? [], [portfolio])
  const groups = useMemo(
    () => buildEntityGroups(entities, projects, groupNames),
    [entities, projects, groupNames],
  )
  const group = findEntityGroup(groups, label)

  // What is on record about IP for each owner of this entity. One read per
  // owner, because the route is per (portfolio, entity). The holder's side of
  // it returns counts only — "2 of 3 owners have acknowledged" — and never an
  // owner's id, name or email, so there is nothing here to be careful with.
  // An arrangement is a record, not a dial: none of this changes what this
  // portfolio can see, which is the inventor's to give, project by project.
  const groupMemberIds = useMemo(
    () => (group?.members ?? []).map((m) => m.entity_id),
    [group],
  )
  const ipQueries = useQueries({
    queries: groupMemberIds.map((entityId) => ({
      queryKey: ['ip-arrangements', aggregatorId, entityId],
      queryFn: () => listEntityIpArrangements(aggregatorId ?? '', entityId),
      enabled: !!aggregatorId,
    })),
  })
  const ipByEntity = new Map<
    string,
    { rows: IpArrangementHolderRow[]; loaded: boolean; error: boolean }
  >()
  groupMemberIds.forEach((entityId, i) => {
    const q = ipQueries[i]
    ipByEntity.set(entityId, {
      rows: q?.data ?? [],
      loaded: !!q && !q.isPending,
      error: !!q?.isError,
    })
  })
  const ipRows = groupMemberIds.flatMap((id) => ipByEntity.get(id)?.rows ?? [])
  // A Billing Owner or an Admin manages the portfolio's own record of an
  // inventor: the group it files them under, and any IP arrangement it puts on
  // record. An Analyst reads. The API refuses an Analyst's proposal outright,
  // so the control is absent for them rather than present-and-refused.
  const canManageEntity = membership?.role === 'billing_owner' || membership?.role === 'admin'

  const disclosures = useMemo(
    () => (Array.isArray(disclosuresData) ? (disclosuresData as DisclosureAgreement[]) : []),
    [disclosuresData],
  )
  const entityNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const e of entities) map.set(e.entity_id, e.entity_name)
    for (const p of projects) map.set(p.entity_id, p.entity_name)
    return map
  }, [entities, projects])

  const memberIds = useMemo(
    () => new Set((group?.members ?? []).map((m) => m.entity_id)),
    [group],
  )
  // This entity's own disclosures: the ones where its side of the ladder is
  // one of this group's owners. Nothing from another entity leaks in.
  const disclosureRows = useMemo(() => {
    const mine = disclosures.filter(
      (d) =>
        (d.my_org_id && memberIds.has(d.my_org_id)) ||
        memberIds.has(d.org_a_id) ||
        memberIds.has(d.org_b_id),
    )
    // subjectEntityIds: this is the page ABOUT this entity, so its side is
    // always the entity and the Counterparty column always names the other
    // party — never the page's own subject.
    return buildDisclosureRows(mine, { entityNameById, subjectEntityIds: memberIds })
  }, [disclosures, memberIds, entityNameById])

  const portfolioProjectIds = useMemo(() => new Set(projects.map((p) => p.project_id)), [projects])
  const ownProjectsNotInPortfolio = useMemo(
    () => (ownProjects ?? []).filter((p) => !p.shared_with_me && !portfolioProjectIds.has(p.project_id)),
    [ownProjects, portfolioProjectIds],
  )

  const consentOwedTo = disclosureRows.find((r) => r.status === 'Consent owed')?.counterpartyLabel ?? null
  const nextStep = rankEntityNextStep(group?.projects ?? [], (group?.members ?? []) as SharingEntity[], consentOwedTo)
  const stepsWaiting = nextStep.kind === 'none' ? 0 : 1

  const entityIntake = group ? intakeForGroup(intakeTags, group.members.map((m) => m.entity_id)) : null

  useSetSpine(
    group
      ? {
          level: 'entity',
          levels,
          youreAt: [group.label],
          stepsWaiting,
          zoomIn: group.projects[0]
            ? {
                title: `Zoom in: Invention (read-only) — ${group.projects[0].name}`,
                to: `/project/${group.projects[0].project_id}`,
              }
            : { title: 'Zoom in: Invention — nothing is shared with you yet' },
          zoomOut: { title: 'Zoom out: Portfolio', to: '/agg?view=overview' },
          crumb: [
            portfolioName || 'Portfolio',
            { label: 'Portfolio', to: '/agg?view=overview' },
            group.label,
          ],
        }
      : null,
  )

  const refreshEntities = () =>
    queryClient.invalidateQueries({ queryKey: ['aggregator-entities', aggregatorId] })

  const handleSwitchOff = (entityId: string) =>
    setExtendedVisibility(entityId, false, 'portfolio-initiated switch-off from the Entity screen')
      .then(refreshEntities)
      .catch(() => undefined)

  const handleEndRelationship = (entityId: string) =>
    endSponsorship(entityId)
      .then(() => {
        closeEditor()
        return refreshEntities()
      })
      .catch(() => undefined)

  const handleAsk = () => {
    if (!group) return
    const owner = group.members[0]?.entity_id
    if (!owner) return
    setAskState('idle')
    askEntityToShareMore(owner, aggregatorId)
      .then(() => setAskState('sent'))
      .catch((error: { response?: { status?: number } }) =>
        setAskState(error?.response?.status === 429 ? 'too-soon' : 'failed'),
      )
  }

  const openEditor = (entityId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('editor', 'sharing')
      next.set('entityId', entityId)
      return next
    })
  }

  const closeEditor = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('editor')
      next.delete('entityId')
      return next
    })
  }

  if (portfolioError || entitiesError) {
    return (
      <div data-part="EntityGroupView">
        <p className="text-sm text-rose-700">
          Could not load this entity right now. Please try again.
        </p>
      </div>
    )
  }

  if (portfolioLoading || entitiesLoading) {
    return (
      <div data-part="EntityGroupView">
        <p className="text-sm text-iflow-900/70">Loading…</p>
      </div>
    )
  }

  if (!group) {
    return (
      <div data-part="EntityGroupView">
        <h1 className="text-2xl font-bold tracking-tight text-iflow-900">{label}</h1>
        <p className="mt-2 text-sm text-iflow-900/80">
          No entity named &ldquo;{label}&rdquo; is in this portfolio.
        </p>
      </div>
    )
  }

  const members = group.members as SharingEntity[]
  const extendedVisibilityMember = members.find((m) => m.extended_visibility)
  const banner =
    nextStep.kind === 'triage' ? (
      <NextStepBanner
        at="Entity"
        verb="triage"
        headline={`${nextStep.project.name} is inbound and unreviewed — triage it.`}
        rule="from inbound"
        tone="amber"
        cta={{ label: 'Open in Inbound →', to: '/agg/inbound' }}
      />
    ) : nextStep.kind === 'awaiting-acceptance' ? (
      <NextStepBanner
        at="Entity"
        verb="wait"
        headline={`${nextStep.ownerName} has not accepted your invitation yet — nothing is shared until they do.`}
        rule="from the invitation · accepting grants nothing by itself"
      />
    ) : nextStep.kind === 'consent' ? (
      <NextStepBanner
        at="Entity"
        verb="consent"
        headline={`${nextStep.counterparty} has consented — the next rung is waiting on ${group.label}.`}
        rule="from the ladder · only the entity can consent"
        tone="amber"
        cta={{ label: 'Open Across portfolios →', to: '/agg/across' }}
      />
    ) : (
      <NextStepBanner
        at="Entity"
        verb="triage"
        none
        headline={`Nothing is waiting on ${group.label}. New inbound and new consents appear here.`}
        rule="from inbound and the ladder"
      />
    )

  const editorEntityId = searchParams.get('entityId')
  const editorEntity =
    searchParams.get('editor') === 'sharing'
      ? members.find((m) => m.entity_id === editorEntityId) ?? members[0]
      : undefined

  // What the drawer's IP section needs: this owner's records, the projects
  // they have granted this portfolio (the only ones the API will accept a
  // proposal against), and which of those carry no arrangement — the figure
  // lists those as "Inventor retains (default)" so the reader sees the whole
  // picture, not only the exceptions.
  const editorIp = editorEntity ? ipByEntity.get(editorEntity.entity_id) : undefined
  const editorGranted = editorEntity
    ? group.projects
        .filter((p) => p.entity_id === editorEntity.entity_id)
        .map((p) => ({ id: p.project_id, name: p.name }))
    : []
  const editorProjectsWithArrangement = new Set(
    (editorIp?.rows ?? [])
      .filter((a) => LIVE_STATUSES.has(a.status))
      .map((a) => a.project_id),
  )
  const editorProjectsWithout = editorGranted.filter(
    (p) => !editorProjectsWithArrangement.has(p.id),
  )

  return (
    <div data-part="EntityGroupView">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-iflow-900">{group.label}</h1>
            <HelpPopover topic="agg-entity-group" size={18} />
            {members.map((member) => (
              <EntityBadge
                key={member.entity_id}
                entityName={member.entity_name}
                byInvitation={entitySharing(member, 0).byInvitation}
                title={
                  entitySharing(member, 0).byInvitation ? byInvitationTitle(portfolioName) : undefined
                }
              />
            ))}
            <span className="text-[12.5px] text-gray-500">{otherOwnerLabel(group.otherOwnerCount)}</span>
            {/* Figure S5 carries an Intake chip and an IP-arrangement chip on
                this row. Both render always: a chip that vanishes when there
                is nothing to say leaves a reader wondering whether the field
                exists, so each says plainly what is true instead. */}
            <span
              data-testid="entity-intake-chip"
              className={`rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${
                entityIntake ? 'bg-gray-100 text-gray-600' : 'bg-gray-50 text-gray-400'
              }`}
              title="The programme intake this portfolio brought the entity in under. Set it in Sharing."
            >
              {entityIntake ?? 'Intake not set'}
            </span>
            {currentArrangement(ipRows) ? (
              <IpArrangementPill arrangements={ipRows} organisation={portfolioName} />
            ) : (
              <span
                data-testid="entity-ip-chip"
                className="rounded-full bg-gray-50 px-2.5 py-0.5 text-[12px] font-semibold text-gray-400"
                title="An IP arrangement records an option, a licence or an assignment agreed outside the platform. None is on record for this entity: the inventor retains their IP, which is the default and needs no arrangement."
              >
                No IP arrangement on record
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-600">
            One entity&apos;s inventions, as far as its owners have shared them with{' '}
            {portfolioName || 'this portfolio'}.
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAsk}
              disabled={members.length === 0 || askState === 'sent'}
              title="A notification, never a grant. Only the owners grant. It never says how many more, and it is limited to one every seven days."
              className="inline-flex items-center gap-1.5 rounded-lg border border-iflow-900/15 bg-white px-3 py-2 text-[13px] font-semibold text-iflow-800 disabled:opacity-50"
            >
              <Mail size={14} aria-hidden /> Ask the owners to share more
            </button>
            <button
              type="button"
              onClick={() => members[0] && openEditor(members[0].entity_id)}
              disabled={members.length === 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-iflow-600 px-3.5 py-2 text-[13px] font-bold text-white hover:bg-iflow-700 disabled:opacity-50"
            >
              <Star size={14} aria-hidden /> Shared with this portfolio
            </button>
          </div>
          {askState !== 'idle' && (
            <p
              data-testid="ask-to-share-result"
              className={`text-[11.5px] ${askState === 'sent' ? 'text-emerald-700' : 'text-amber-700'}`}
            >
              {askState === 'sent'
                ? 'Asked. The owners decide what to share; nothing has changed yet.'
                : askState === 'too-soon'
                  ? 'You have already asked these owners in the last seven days.'
                  : 'The request could not be sent. Please try again.'}
            </p>
          )}
        </div>
      </div>

      {banner}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <EntityInventions
            projects={group.projects}
            portfolioName={portfolioName}
            ownerNameById={entityNameById}
          />
          <EntityMap
            layout={mapData?.layout ?? {}}
            memberProjectIds={mapData?.member_project_ids ?? []}
            entityProjectIds={group.projects.map((p) => p.project_id)}
            projectNames={new Map(projects.map((p) => [p.project_id, p.name]))}
            documentCounts={new Map(projects.map((p) => [p.project_id, p.document_count]))}
            overlapGroups={overlapData?.groups ?? []}
            entityLabel={group.label}
          />
          <EntityDisclosures rows={disclosureRows} entityLabel={group.label} />
        </div>

        <div className="flex flex-col gap-4">
          <WhatYouCanSee
            group={group}
            extendedVisibility={!!extendedVisibilityMember}
            extendedVisibilityGrantedBy={extendedVisibilityMember?.entity_name}
            portfolioName={portfolioName}
            onSwitchOff={() =>
              extendedVisibilityMember && handleSwitchOff(extendedVisibilityMember.entity_id)
            }
          />
          <YoursNotPartOfPortfolio projects={ownProjectsNotInPortfolio} variant="entity" />
          <SharedWithPortfolio
            group={group}
            portfolioName={portfolioName}
            onManage={openEditor}
          />
        </div>
      </div>

      {editorEntity && (
        <SharingEditor
          entity={editorEntity}
          groupLabel={group.label}
          portfolioName={portfolioName}
          grantedCount={group.projects.filter((p) => p.entity_id === editorEntity.entity_id).length}
          intake={intakeFor(intakeTags, editorEntity.entity_id) ?? ''}
          onIntakeChange={(value) => setIntake(editorEntity.entity_id, value)}
          onGroupLabelChange={
            canManageEntity
              ? (value) => {
                  const target = editorEntity.entity_id
                  void setGroupName(target, value).then(() => {
                    // The route carries the group's name, so renaming it moves
                    // this inventor to a different page. Follow them there,
                    // keeping the drawer open, rather than leaving the reader
                    // on a group the inventor is no longer in.
                    const next = value.trim()
                    if (!next || next === group.label) return
                    navigate(
                      `${entityGroupPath(next)}?editor=sharing&entityId=${encodeURIComponent(target)}`,
                      { replace: true },
                    )
                  })
                }
              : undefined
          }
          onClose={closeEditor}
          onSwitchOffExtendedVisibility={() => handleSwitchOff(editorEntity.entity_id)}
          onEndRelationship={() => handleEndRelationship(editorEntity.entity_id)}
          onInviteAsAnalyst={() => navigate('/agg/team?invite=analyst')}
          canEndRelationship={canEndRelationship}
          ipArrangements={editorIp?.rows ?? []}
          ipLoaded={!!editorIp?.loaded}
          ipError={!!editorIp?.error}
          ipGrantedProjects={editorGranted}
          ipProjectsWithout={editorProjectsWithout}
          onProposeIpArrangement={
            canManageEntity
              ? (proposal) =>
                  proposeIpArrangement(aggregatorId ?? '', editorEntity.entity_id, proposal).then(() =>
                    queryClient.invalidateQueries({
                      queryKey: ['ip-arrangements', aggregatorId, editorEntity.entity_id],
                    }),
                  )
              : undefined
          }
        />
      )}
    </div>
  )
}
